/**
 * Design tokens for SmartMixin webapp
 * Centralized design system values for consistent UI
 *
 * - The SEMANTIC layer (`semanticColors`, `typeScale`, `space`, `radius`,
 *   `elevation`, `motion`, `shell`, `layers`), from the signed-off spec in
 *   `.scratch/webapp-design-system/target.html`, section 1. The colour roles
 *   both apps share (brand, ink, status tints, weather families, day curve)
 *   come from `colorRoles` in `@smartmixin/core`; what is here is the
 *   webapp-only half: surfaces, lines, text greys, chart grid, tooltip, scrim.
 *   `createAppTheme` maps all of it onto the MUI palette.
 *
 * - The base scales `createAppTheme` reads (font families and weights, the
 *   heading sizes, breakpoints, the toolbar height) and `secondaryFill`.
 *
 * - Depictions and data colormaps: the moon, the twilight marker, the AQI and
 *   WBGT bands, the warning severities. Not roles: each is the colour of a
 *   thing or a value, measured where it is drawn.
 *
 * The pre-redesign aliases (`weatherColors`, `statusColors`, `brandColors`,
 * `accentColors`, `dayCurveColors`, `zIndex`, ...) were deleted by the final
 * design-system MR; screens read the roles on the palette instead
 * (`theme.palette.family.*`, `ink`, `dayCurve`, `success`/`warning`/`error`).
 */

/**
 * Webapp-only colour roles, per theme. Use rules (spec section 1):
 *
 * - `surface.page` is behind the cards (also the sidebar, rail and table
 *   header); `surface.card` is cards, menus, dialogs, popovers, top bars.
 * - `line.hairline` is DECORATIVE only (card edge, row rules) at ~1.2:1. It
 *   must never be the only boundary of a control — that is `line.control`,
 *   which clears 3:1 on both the page and the card (light 3.19 / 3.43).
 * - `line.floating` is the edge of a panel or control floating over a map
 *   (`mapPanelSx`). Map tiles are not our surfaces: on the light basemap's
 *   water (#c2c8ca) `line.control` measured 2.02:1, so the light edge is
 *   darker (3.51:1 on water, 5.34:1 on land). The dark theme's card
 *   (#16191c) already stands 10.43:1 off that water, so its edge keeps
 *   `line.control`'s value and is informative there.
 * - `text.primary` carries every value, including every measured value; a
 *   value is never drawn in a hue. `text.secondary` is labels, captions, axis
 *   ticks, units, placeholders.
 * - `chart.grid` is decorative. `tooltip` is inverse. `scrim` is the dialog
 *   backdrop.
 */
export const semanticColors = {
  light: {
    surface: {page: '#f6f7f8', card: '#ffffff'},
    line: {hairline: '#e3e6e8', control: '#838c92', floating: '#5c656c'},
    text: {
      primary: '#16191c',
      secondary: '#5c656c',
      disabled: 'rgba(22, 25, 28, 0.38)',
    },
    action: {
      hover: 'rgba(22, 25, 28, 0.04)',
      selected: 'rgba(51, 181, 229, 0.12)',
      disabledBackground: 'rgba(22, 25, 28, 0.08)',
    },
    chart: {grid: '#eceef0'},
    tooltip: {bg: '#16191c', fg: '#ffffff'},
    scrim: 'rgba(16, 24, 32, 0.40)',
  },
  dark: {
    surface: {page: '#0f1113', card: '#16191c'},
    line: {hairline: '#2a2f33', control: '#6b747b', floating: '#6b747b'},
    text: {
      primary: '#e6e8ea',
      secondary: '#9aa3aa',
      disabled: 'rgba(230, 232, 234, 0.38)',
    },
    action: {
      hover: 'rgba(230, 232, 234, 0.06)',
      selected: 'rgba(51, 181, 229, 0.12)',
      disabledBackground: 'rgba(230, 232, 234, 0.10)',
    },
    chart: {grid: '#23282c'},
    tooltip: {bg: '#e6e8ea', fg: '#16191c'},
    scrim: 'rgba(0, 0, 0, 0.60)',
  },
} as const;

/**
 * Type scale "T2": Inter variable, self-hosted, tabular figures on every
 * number. Sizes in px; the theme converts to rem. `pro badge` at 10px is the
 * single sub-12px exception.
 */
export const typeScale = {
  caption: {fontSize: 12, lineHeight: 16, fontWeight: 400},
  data: {fontSize: 13, lineHeight: 18, fontWeight: 400, tnum: true},
  body: {fontSize: 14, lineHeight: 20, fontWeight: 400},
  title: {fontSize: 14, lineHeight: 20, fontWeight: 600},
  kpi: {fontSize: 18, lineHeight: 24, fontWeight: 600, tnum: true},
  h1: {fontSize: 22, lineHeight: 28, fontWeight: 600, letterSpacing: -0.2},
  h1Phone: {fontSize: 20},
  hero: {
    fontSize: 48,
    lineHeight: 1,
    fontWeight: 600,
    letterSpacing: -1,
    tnum: true,
  },
  display: {
    fontSize: 52,
    lineHeight: 1.05,
    fontWeight: 650,
    letterSpacing: -1.4,
  },
  displayPhone: {fontSize: 32},
  proBadge: {fontSize: 10, lineHeight: 1, fontWeight: 700, letterSpacing: 0.5},
} as const;

/** 4-pt spacing, in px. Keys are the step, not the pixel value. */
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  12: 48,
} as const;

/** Corner radii, in px. */
export const radius = {
  xs: 4, // chips, badges, tooltips, status
  sm: 6, // buttons, inputs, segmented, nav items
  md: 8, // cards, menus, popovers, map panels
  lg: 10, // dialogs
  round: 9999, // avatar, switch, status dot, radio
} as const;

/**
 * Three elevations. Resting surfaces get a hairline and NO shadow; floating
 * ones (menus, popovers, tooltips, map panels) add a 1px `line.control` edge.
 */
export const elevation = {
  resting: 'none',
  floating: {
    light: '0 6px 16px rgba(16, 24, 32, 0.12)',
    dark: '0 6px 16px rgba(0, 0, 0, 0.5)',
  },
  modal: '0 12px 32px rgba(0, 0, 0, 0.25)',
} as const;

/** Durations in ms. Under prefers-reduced-motion: opacity only. */
export const motion = {
  easing: 'cubic-bezier(0.2, 0, 0, 1)',
  state: 120,
  enter: 200,
  exit: 150,
} as const;

/**
 * App shells by width (logged in): bottom nav below 900, a 64px rail from 900,
 * the 232px sidebar from 1200. Logged out: the 56px public top bar
 * everywhere. The breakpoints themselves are `breakpoints` below (unchanged,
 * and MUI's defaults).
 */
export const shell = {
  railMinWidth: 900,
  sidebarMinWidth: 1200,
  railWidth: 64,
  sidebarWidth: 232,
  /** The fixed top bar, its 1px hairline border included. */
  topBarHeight: 56,
  bottomNavHeight: 56,
} as const;

/** z-index: MUI's defaults, kept. Map panels sit inside the map's own stack. */
export const layers = {
  mapPanel: 10,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
} as const;

/* -------------------------------------------------------------------------- */
/* Base scales createAppTheme builds the MUI theme from                       */
/* -------------------------------------------------------------------------- */

/**
 * MUI's `secondary` slot: the orange the chart presets draw their accent
 * series in (solar, grassland, lightning in `@smartmixin/core` Charts) and
 * the icons' `color="secondary"`. A FILL, never text: 3.10:1 on the light
 * card (a known failure in `__tests__/a11y/contrast.test.ts`).
 */
export const secondaryFill = '#fb5f26';

// Typography scale
export const typography = {
  // "Inter Variable" is the family @fontsource-variable/inter registers (the
  // bundled, self-hosted face); plain 'Inter' only catches a local install.
  fontFamily:
    "'Inter Variable', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",

  // Font weights
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },

  // Font sizes
  sizes: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    md: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },
};

// Navigation dimensions
export const navigation = {
  bottomNavHeight: 56,
  appBarHeight: 64,
  appBarHeightMobile: 56,
};

// Breakpoints
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

/* -------------------------------------------------------------------------- */
/* Depictions and data colormaps                                              */
/*                                                                            */
/* Colours of objects and data scales, not surfaces or inks: the moon, the    */
/* sky, the AQI and WBGT bands, official warning severities. Each is measured */
/* where it is drawn (`__tests__/a11y/contrast.test.ts`).                     */
/* -------------------------------------------------------------------------- */

/**
 * The moon disc, per theme.
 *
 * Ported from `apps/mobile/src/theme/index.tsx` so the two apps draw the same
 * moon. Not derived from the MUI palette: the disc is a depiction of an object,
 * not a surface, and tinting it with `primary` makes it a blue circle.
 *
 * `rim` is load-bearing rather than decorative — without it a full moon
 * dissolves into the light card and a new moon into the dark one. The lit face
 * measures 1.08:1 against the light card, which is exactly the problem; the rim
 * carries 6.00:1 light and 6.23:1 dark and is what makes the disc an object.
 */
export const moonColors = {
  light: {
    lit: '#fdf6e3',
    litEdge: '#cbc3ac',
    dark: '#4a5568',
    ink: '#2d3748',
    rim: '#5b6470',
  },
  dark: {
    lit: '#fdf6e3',
    litEdge: '#c4bca6',
    dark: '#2b3444',
    ink: '#1a2130',
    rim: '#8b97a8',
  },
} as const;

/**
 * The moon's altitude arc: tonight's pass, drawn as a shape.
 *
 * Violet rather than `primary`, which is what mobile uses: the sun card sits
 * directly above the moon one in the same dashboard column and already spends
 * the `#33B5E5` tint on its day-length chip, and one colour carrying two
 * meanings in the same column is worse than a second colour.
 *
 * `stroke` is a 2px line and `fill` a wash beneath it, so the gate is the 3:1
 * graphics threshold, not 4.5:1. Measured against `background.paper`:
 * `#6d43e0` is 5.95:1 light and `#b39dff` is 8.07:1 dark. Raw `#7C4DFF` is
 * 3.84:1 on the dark paper — passing, but thin at 2px, which is why the dark
 * tone is lightened rather than shared.
 *
 * `horizon` is held to the same 3:1, and is the reason these are not the
 * `divider` token: an altitude is measured *from* the horizon, so the line is
 * meaningful rather than decorative, and outside the up-window the area path
 * has zero height, leaving it on bare paper with nothing to help it. Composited
 * against `background.paper`: `rgba(0,0,0,.42)` is 3.03:1 light and
 * `rgba(255,255,255,.42)` is 4.11:1 dark. The 0.26/0.30 first drafted measured
 * 1.88:1 and 2.67:1 — both failing, on the one line the chart cannot do without.
 *
 * `upWindow` shades the hours the moon is actually above the horizon and is
 * deliberately the faintest thing here: it is context for the curve, not a
 * second series, and it is the only element with no contrast floor to meet.
 */
export const moonArcColors = {
  light: {
    stroke: '#6d43e0',
    fill: 'rgba(124, 77, 255, 0.20)',
    upWindow: 'rgba(124, 77, 255, 0.08)',
    horizon: 'rgba(0, 0, 0, 0.42)',
  },
  dark: {
    stroke: '#b39dff',
    fill: 'rgba(179, 157, 255, 0.22)',
    upWindow: 'rgba(179, 157, 255, 0.10)',
    horizon: 'rgba(255, 255, 255, 0.42)',
  },
} as const;

/**
 * The "now" marker across the sun page's twilight band.
 *
 * The band is a depiction of the sky (`Sun.skyAt`), not a themed surface, so
 * the marker is the same in both themes: a white core with a dark hairline
 * either side, so one of the two always clears the 3:1 graphics floor:
 * on the palest sky (`Sun.skyAt` at the zenith, #a8dbf5) the core is 1.49:1
 * and the edge carries it at 4.28:1; on the night sky (#0d1330) the edge is
 * 1.09:1 and the core carries it at 18.21:1. Composited, and asserted in
 * `__tests__/a11y/contrast.test.ts`. Was written out as literals in
 * `TwilightBand.tsx`.
 */
export const twilightMarkerColors = {
  core: '#ffffff',
  edge: 'rgba(0, 0, 0, 0.55)',
} as const;

/**
 * US EPA AQI bands, good to hazardous: a data colormap, the same hexes as the
 * mobile `aqiUtils`. Drawn only as the arc of the dashboard's AQI gauge, never
 * as text — the band is always printed in words beside it, because several of
 * these (the moderate yellow above all) fail 3:1 on the light card.
 */
export const aqiBandColors = [
  '#159868',
  '#FEDD47',
  '#FD9941',
  '#CA0A37',
  '#651297',
  '#7D0425',
] as const;

// WBGT heat stress scale (ISO 7243 / Liljegren)
export type WbgtHeatStressLevel = 0 | 1 | 2 | 3 | 4;

export type WbgtHeatStressInfo = {
  category: WbgtHeatStressLevel;
  color: string;
  messageKey:
    | 'heatStressNone'
    | 'heatStressModerate'
    | 'heatStressStrong'
    | 'heatStressVeryStrong'
    | 'heatStressExtreme';
};

export const wbgtHeatStressScale: Record<
  WbgtHeatStressLevel,
  Omit<WbgtHeatStressInfo, 'category'>
> = {
  0: {color: '#4CAF50', messageKey: 'heatStressNone'},
  1: {color: '#FFC107', messageKey: 'heatStressModerate'},
  2: {color: '#FF9800', messageKey: 'heatStressStrong'},
  3: {color: '#F44336', messageKey: 'heatStressVeryStrong'},
  4: {color: '#B71C1C', messageKey: 'heatStressExtreme'},
};

/**
 * Get WBGT heat stress info (category, color, i18n key) from Celsius value
 */
export const getWbgtHeatStressInfo = (
  wbgtCelsius: number,
): WbgtHeatStressInfo => {
  let category: WbgtHeatStressLevel;
  if (wbgtCelsius < 25) category = 0;
  else if (wbgtCelsius < 28) category = 1;
  else if (wbgtCelsius < 29.5) category = 2;
  else if (wbgtCelsius < 31) category = 3;
  else category = 4;

  return {category, ...wbgtHeatStressScale[category]};
};

/**
 * Get status label based on time since last update
 */
export const getStationStatusLabel = (
  minutesSinceUpdate: number,
): 'online' | 'stale' | 'offline' => {
  if (minutesSinceUpdate <= 30) return 'online';
  if (minutesSinceUpdate <= 240) return 'stale';
  return 'offline';
};

/**
 * Official weather warnings: one colour set per core `WarningTone`
 * (`WeatherWarnings.severityTone`), per theme. The same hexes as mobile's
 * `warning*` tokens in `apps/mobile/src/theme/index.tsx`, so a severity reads
 * the same in both apps.
 *
 * - `band` / `onBand`: the severity band (banner, detail pane header) and the
 *   banner's "Show hazards" toggle, whose border is `currentColor`. MeteoAlarm
 *   yellow / orange / red; the same hue AND ink in both themes.
 * - `chip` / `onChip`: the period-row chip, per theme.
 * - `lane`: a lane segment (the band hue, as its own role).
 * - `mapFill` / `mapNeighbourFill`: the own area (full hue) and the other areas
 *   (the hue at 14 % over the tiles). Same in both themes: the basemap is the
 *   light OpenFreeMap positron style in both.
 *
 * Measured in `__tests__/a11y/contrast.test.ts`, including the fills that fail
 * 3:1 by design and the cue that carries each. Band word 11.20 / 7.34 / 5.62
 * (moderate / severe / extreme), 13.22 light / 7.20 dark (minor).
 */
type SeverityTone = {
  band: string;
  onBand: string;
  chip: string;
  onChip: string;
  lane: string;
  mapFill: string;
  mapNeighbourFill: string;
};

const severityTone = (
  mapHue: string,
  mapNeighbourFill: string,
  colors: Pick<SeverityTone, 'band' | 'onBand' | 'chip' | 'onChip'>,
): SeverityTone => ({
  ...colors,
  lane: colors.band,
  mapFill: mapHue,
  mapNeighbourFill,
});

// MeteoAlarm hues. Minor's map hue is a grey that reads on the tiles; its band
// and chip are each theme's neutral surface.
const moderate = (c: Pick<SeverityTone, 'chip' | 'onChip'>) =>
  severityTone('#FFD600', 'rgba(255, 214, 0, 0.14)', {
    band: '#FFD600',
    onBand: '#2a2200',
    ...c,
  });
const severe = (c: Pick<SeverityTone, 'chip' | 'onChip'>) =>
  severityTone('#FB8C00', 'rgba(251, 140, 0, 0.14)', {
    band: '#FB8C00',
    onBand: '#2a1500',
    ...c,
  });
const extreme = (c: Pick<SeverityTone, 'chip' | 'onChip'>) =>
  severityTone('#C62828', 'rgba(198, 40, 40, 0.14)', {
    band: '#C62828',
    onBand: '#ffffff',
    ...c,
  });
const minor = (c: Pick<SeverityTone, 'band' | 'onBand' | 'chip' | 'onChip'>) =>
  severityTone('#70787d', 'rgba(112, 120, 125, 0.14)', c);

const minorLight = minor({
  band: '#dce3e9',
  onBand: '#191c1e',
  chip: '#dce3e9',
  onChip: '#40484c',
});
const minorDark = minor({
  band: '#3f4948',
  onBand: '#e0e3e2',
  chip: '#3f4948',
  onChip: '#bec9c7',
});

export const severityColors = {
  light: {
    warningMinor: minorLight,
    warningModerate: moderate({chip: '#fbefb0', onChip: '#6b5600'}),
    warningSevere: severe({chip: '#fde0c4', onChip: '#a04500'}),
    warningExtreme: extreme({chip: '#fddcd8', onChip: '#b3141b'}),
    // An unparsed severity is drawn as Minor, never as a guessed level.
    warningUnknown: minorLight,
  },
  dark: {
    warningMinor: minorDark,
    warningModerate: moderate({chip: '#3b3518', onChip: '#f2c94c'}),
    warningSevere: severe({chip: '#46301c', onChip: '#ffa25c'}),
    warningExtreme: extreme({chip: '#4a2426', onChip: '#ff8a80'}),
    warningUnknown: minorDark,
  },
} as const;

/**
 * The warnings timeline ("lane") under each hazard on `/warnings`: the track
 * the severity segments sit on, the midnight ticks, and the Now line with its
 * halo. Per theme, measured against THIS app's `background.paper` (white /
 * rgb(22, 25, 28)) in `__tests__/a11y/contrast.test.ts`.
 *
 * - `track`: deliberately faint (1.13:1 light, 1.25:1 dark on paper) — it is
 *   background, and the segments on it are decorative: the period rows under
 *   the lane are their text twin.
 * - `nowLine` + `nowHalo`: a 2 px line on a paper-coloured halo, so that
 *   against every segment the line OR the halo clears 3:1 (light: the line,
 *   3.05:1 at worst on red; dark: the halo on yellow and orange, the line on
 *   red at 4.35:1).
 * - `tick`: the dashed midnight tick; its label is `text.secondary`.
 */
export const severityLaneColors = {
  light: {
    track: '#eef1f4',
    nowLine: '#191c1e',
    nowHalo: '#ffffff',
    tick: '#70787d',
  },
  dark: {
    track: '#262c2e',
    nowLine: '#e0e3e2',
    nowHalo: '#16191c',
    tick: '#899295',
  },
} as const;

/**
 * The warning map's strokes. One value: the basemap (OpenFreeMap positron, a
 * light style) is the same in both themes.
 */
export const severityMapColors = {
  // Own-area outline (2.5 px), location pin ring, own-area name.
  outline: '#191c1e',
  // 6 px casing under the outline, and the halo behind the name.
  casing: '#ffffff',
  // Boundary between neighbour areas.
  neighbourLine: '#80868b',
  // The basemap credit, on a 75 % white plate over the map (target `.mapattr`).
  attribution: '#3d3d3d',
  attributionPlate: 'rgba(255, 255, 255, 0.75)',
  // Positron's land (`background`): the map box shows it while tiles load.
  land: '#f2f3f0',
  // The round "Show all N areas on the map" toggle's rim. Decorative: 1.52:1
  // on land, so the ink glyph (17.13:1 on the white plate) is what identifies
  // the control (1.4.11).
  controlBorder: '#c0c8cd',
} as const;
